import { useState, useCallback } from 'react';
import type { InstructionActionType } from '../constants';
import type { AssignActionData, InstructionActionData, InstructionUnassignTarget } from '../types';
import { upgradeLegacyAction } from '../utils/instruction-actions';

interface UseInstructionActionsReturn {
  actionData: InstructionActionData[] | null; // Array of actions
  activeAction: InstructionActionType | null;
  isActionExpanded: boolean;
  editingAction: InstructionActionData | null;
  /** Opens the composer for a new action (Send Email, Private Note, Slack). */
  handleActionClick: (actionType: InstructionActionType) => void;
  /** Adds the Assign, or replaces the one there: there is no composer, the pick is the action. */
  handleAssign: (assign: AssignActionData) => void;
  /** Adds the Unassign for this target, or retargets the one there; a blank target is the brand's default. */
  handleUnassign: (target: InstructionUnassignTarget) => void;
  /** Takes an action of this type off the instruction. */
  handleActionRemove: (actionType: InstructionActionType) => void;
  handleActionSave: (actionData: InstructionActionData) => void;
  handleActionCancel: () => void;
  clearAction: () => void;
  initializeWithExisting: (existingActions: InstructionActionData[] | null | undefined) => void;
  handleActionEdit: (actionType: InstructionActionType) => void;
}

/** The actions with this one in place of any of its type. */
const replaceAction = (prev: InstructionActionData[] | null, action: InstructionActionData): InstructionActionData[] => [
  ...(prev?.filter((existing) => existing.type !== action.type) ?? []),
  action
];

export const useInstructionActions = (): UseInstructionActionsReturn => {
  // Store array of actions
  const [actionData, setActionData] = useState<InstructionActionData[] | null>(null);
  const [activeAction, setActiveAction] = useState<InstructionActionType | null>(null);
  const [isActionExpanded, setIsActionExpanded] = useState(false);
  const [editingAction, setEditingAction] = useState<InstructionActionData | null>(null);

  const handleActionClick = useCallback((actionType: InstructionActionType) => {
    setEditingAction(null); // Clear editing state for new action
    setActiveAction(actionType);
    setIsActionExpanded(true);
  }, []);

  const handleAssign = useCallback((assign: AssignActionData) => {
    setActionData((prev) => replaceAction(prev, assign));
  }, []);

  const handleUnassign = useCallback((target: InstructionUnassignTarget) => {
    setActionData((prev) => replaceAction(prev, target ? { type: 'KIM_UNASSIGN', target } : { type: 'KIM_UNASSIGN' }));
  }, []);

  const handleActionRemove = useCallback((actionType: InstructionActionType) => {
    setActionData((prev) => prev?.filter((action) => action.type !== actionType) ?? null);
  }, []);

  const handleActionSave = useCallback((data: InstructionActionData) => {
    setActionData((prev) => replaceAction(prev, data));
    setActiveAction(null);
    setIsActionExpanded(false);
    setEditingAction(null);
  }, []);

  const handleActionCancel = useCallback(() => {
    setActiveAction(null);
    setIsActionExpanded(false);
    setEditingAction(null);
  }, []);

  const clearAction = useCallback(() => {
    setActionData(null);
    setActiveAction(null);
    setIsActionExpanded(false);
    setEditingAction(null);
  }, []);

  const initializeWithExisting = useCallback((existingActions: InstructionActionData[] | null | undefined) => {
    // A legacy Assign to User loads as an Assign to Agent, and is saved in that shape.
    if (existingActions && existingActions.length > 0) setActionData(existingActions.map(upgradeLegacyAction));
  }, []);

  const handleActionEdit = useCallback(
    (actionType: InstructionActionType) => {
      // Find the existing action to edit
      const existingAction = actionData?.find((a) => a.type === actionType);
      if (existingAction) {
        setEditingAction(existingAction);
        setActiveAction(actionType);
        setIsActionExpanded(true);
      }
    },
    [actionData]
  );

  return {
    actionData,
    activeAction,
    isActionExpanded,
    editingAction,
    handleActionClick,
    handleAssign,
    handleUnassign,
    handleActionRemove,
    handleActionSave,
    handleActionCancel,
    clearAction,
    initializeWithExisting,
    handleActionEdit
  };
};
